import * as React from 'react';
import ReactDOM from 'react-dom';
import * as L from 'leaflet';
import { StyledLefletMap } from './Leaflet.style';
import { LeafletContext, leafletContext, LeafletTooltipContext } from './Leaflet.context';
import { useLeafletMap } from './Leaflet.hooks';
import cn from 'classnames';
import 'leaflet/dist/leaflet.css';
import { noop } from 'lodash';
import { IGeoJSON } from '../ChartMap/types';

export const LeafletMap = leafletContext(function Leaflet(props: any) {
  const { id, className, children, center, zoomLevel, options = {} } = props;

  const ref = React.useRef<HTMLElement | null>(null);
  const [leafletMap, setLeafletMap] = useLeafletMap();
  React.useEffect(function didMount() {
    if (ref.current !== null) {
      const map = L.map(ref.current, {
        minZoom: 2,
        maxZoom: 8,
        maxBounds: L.latLngBounds(L.latLng(100, 500), L.latLng(-100,-500)),
        trackResize: true,
        ...options
      });

      if (center && zoomLevel) {
        map.setView(center, zoomLevel);
      } else {
        map.fitWorld();
      }

      setLeafletMap(map);

      const handlerResize = () => {
        map.invalidateSize();
        map.fitWorld();
      }
      ref.current.addEventListener('resize', handlerResize);
      let resizeObserver: any = null;
      if (window['ResizeObserver']) {
        resizeObserver = new window['ResizeObserver'](handlerResize);
        resizeObserver.observe(ref.current);
      } else {
        let height: any = null;
        let width: any = null;
        resizeObserver = setInterval(() => {
          const rect = ref.current?.getBoundingClientRect();
          if (rect && height !== null && width !== null) {
            if (rect.height !== height || rect.width !== width) {
              handlerResize();
            }
          }
          height = rect?.height;
          width = rect?.width;
        }, 500);
      }


      return () => {
        if (ref.current) {
          ref.current.addEventListener('resize', handlerResize);
          if (window['ResizeObserver']) {
            resizeObserver.unobserve(ref.current);
          } else {
            clearInterval(resizeObserver);
          }
        }
      }
    }
  }, []);

  return <StyledLefletMap id={id} className={cn(className, "leaflet-map")} ref={ref as any}>
    {children}
  </StyledLefletMap>
});

export const LeafletGeoJSON = (props: any) => {
  const { children, geoJSON, options, pipeline, onMount = noop } = props;

  const [leafletMap] = useLeafletMap();
  React.useEffect(function didMount() {
    if (leafletMap !== null) {
      const mutateGeoJSON: IGeoJSON = pipeline?.reduce((acc, pipe) => pipe(acc), geoJSON) || geoJSON;
      L.geoJSON(mutateGeoJSON, { ...options, type: 'geo-layer' } as any).addTo(leafletMap);
      onMount(leafletMap, mutateGeoJSON);
      return () => {
        leafletMap?.eachLayer(layer => {
          leafletMap.removeLayer(layer);
        })
      }
    }
  }, [leafletMap, pipeline]);

  return <>
    {children}
  </>
};

export const LeafletMarker = (props: any) => {
  const { coordinates, iconSize, iconAnchor, children } = props;
  const target = React.useRef(document.createElement('div'));

  const [leafletMap] = useLeafletMap();
  React.useEffect(function () {
    if (leafletMap !== null) {
      let marker: L.Marker | null = null;
      const handlerLayerAdd = ({ layer }) => {
        if (['geo-layer'].includes(layer.options['type']) && layer._layers) {
          const svgNode = target.current.querySelector('svg')!;

          if (svgNode) {
            const base64 = window.btoa(new XMLSerializer().serializeToString(svgNode));
            const icon = L.icon({
              iconUrl: `data:image/svg+xml;base64,${base64}`,
              iconSize,
              iconAnchor,
            });
            marker = L.marker(coordinates, {
              type: "marker",
              icon
            } as any);
            marker.addTo(leafletMap);
          }
        }
      };

      leafletMap.addEventListener("layeradd", handlerLayerAdd);
      leafletMap.addEventListener("layerremove", ({ layer }) => {
        if (layer._layers) {
          ['geo-layer'].includes(layer.options['type']) && marker?.remove();
        }
      });
      return () => {
        leafletMap.removeEventListener("layeradd", handlerLayerAdd);
      }
    }
  }, [leafletMap, coordinates, target]);

  return ReactDOM.createPortal(children, target.current);
};

export class LeafletComponent extends React.PureComponent<any> {
  static contextType = LeafletContext;
  elem: HTMLElement;
  container: HTMLElement;
  component: L.Control;
  mounted: boolean = false;

  constructor(props: any) {
    super(props);
    const { options, id } = this.props as any;
    const classes = ['leaflet-component', props.className].filter(Boolean).join(' ');
    this.elem = L.DomUtil.create('div', classes);
    if (id) {
      this.elem.id = id;
    }
    this.container = L.DomUtil.create('div');
    this.component = new L.Control(options);
  }

  componentDidMount() {
    this._tryMountControl();
  }

  componentDidUpdate() {
    this._tryMountControl();
  }

  componentWillUnmount() {
    const [leafletMap] = this.context;
    if (leafletMap && this.component) {
      leafletMap.removeControl(this.component);
    }
  }

  render() {
    return ReactDOM.createPortal(this.props.children, this.elem);
  }

  private _tryMountControl() {
    if (!this.mounted) {
      const [leafletMap] = this.context;
      if (leafletMap !== null) {
        this.mounted = true;
        this.component.onAdd = () => {
          this.container.appendChild(this.elem);
          return this.container;
        };

        this.component.addTo(leafletMap);
      }
    }
  }
}

export class LeafletTooltip extends React.PureComponent<any, any> {
  static contextType = LeafletContext;
  elem: HTMLElement;
  mounted: boolean = false;
  portal: React.ReactPortal;

  state = {
    layer: null
  }

  constructor(props: any) {
    super(props);
    const { id } = this.props as any;
    const classes = ['leaflet-tooltip', props.className].filter(Boolean).join(' ');
    this.elem = L.DomUtil.create('div', classes);
    if (id) {
      this.elem.id = id;
    }
    this.portal = ReactDOM.createPortal(this.props.children, this.elem);
  }

  componentDidMount() {
    this._tryMountTooltip();
  }

  componentDidUpdate() {
    this._tryMountTooltip();
  }

  render() {
    return <LeafletTooltipContext.Provider value={this.state.layer}>
      {this.portal}
    </LeafletTooltipContext.Provider>;
  }

  private _tryMountTooltip() {
    const leafletMap = this.context[0] as L.Map;
    if (leafletMap !== null && !this.mounted) {
      this._mount();
    }
  }

  private _mount() {
    const { layerId } = this.props as any;
    this.mounted = true;
    const leafletMap = this.context[0] as L.Map;
    leafletMap.addEventListener("layeradd", (event) => {
      let rootLayer: L.Layer | null = null;
      leafletMap!.eachLayer(layer => {
        const options = layer['options'] as any;
        if (options?.id === layerId && !layer['feature']) {
          rootLayer = layer;
        }
      });

      const options = rootLayer?.['options'] as any;
      if (rootLayer && !options?.['mounted']) {
        options['mounted'] = true;
        const layers = Object.keys(rootLayer['_layers']).map(e => rootLayer!['_layers'][e]) as L.Layer[];
        layers.forEach((layer, i) => {
          const tooltip = L.tooltip({
            sticky: true,
          }, layer);
          tooltip.setContent(this.elem);
          const handlerOpen = () => {
            this.setState({ layer })
            if (this.elem.parentElement) {
              this.elem.parentElement.style.padding = '0';
              const rect = this.elem.getBoundingClientRect();
              this.elem.parentElement.style.width = `${rect.width}px`;
              this.elem.parentElement.style.height = `${rect.height}px`;
            }
          };
          layer.addEventListener("tooltipopen", handlerOpen);
          layer.addEventListener("remove", () => this.mounted = false);
          layer.bindTooltip(tooltip);
        });
      }
    });
  }
}